import {
  acquireAvailableAliquot,
  getMemberNumber,
  getMembers,
  transferMemberAliquot,
  verifyMemberAdmission,
  type MemberNumberResponse,
  type MemberListItemContract,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalMetricCard,
  CanonicalMetricGrid,
  CanonicalFileDropzone,
  CanonicalModal,
  SearchSelectField,
  StatePanel,
  StatusBadge,
  canonicalStates,
  formatDateTime,
} from '@urbanik/ui';
import { ArrowRightLeft, History, Layers3, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
import './member-aliquots.css';
type Assignment = MemberNumberResponse['history'][number];
export function MemberAliquotsPanel({ memberId }: { memberId: string }) {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading'),
    [data, setData] = useState<MemberNumberResponse | null>(null),
    [revision, setRevision] = useState(0),
    [transfer, setTransfer] = useState<Assignment | null>(null),
    [adding, setAdding] = useState(false),
    [verifying, setVerifying] = useState<Assignment | null>(null),
    [actionSuccess, setActionSuccess] = useState<{ title: string; message: string } | null>(null);
  const resumeParams = new URLSearchParams(window.location.hash.split('?')[1] ?? ''),
    resumeAssignmentId = resumeParams.get('transfer'),
    resumeBuyerId = resumeParams.get('target'),
    resumeBuyerQuery = resumeParams.get('targetQuery') ?? '';
  const load = () => {
    setState('loading');
    getMemberNumber(apiBase, memberId)
      .then((v) => {
        setData(v);
        setState('success');
      })
      .catch(() => setState('error'));
  };
  useEffect(load, [memberId, revision]);
  useEffect(() => {
    if (!data || !resumeAssignmentId) return;
    const assignment = data.history.find(
      (item) => item.id === resumeAssignmentId && item.endedAt === null,
    );
    if (assignment) setTransfer((current) => current ?? assignment);
  }, [data, resumeAssignmentId]);
  const closeTransfer = () => {
    setTransfer(null);
    if (resumeAssignmentId) window.location.hash = `#socios/${memberId}?tab=aliquots`;
  };
  const active = useMemo(() => data?.history.filter((x) => x.endedAt === null) ?? [], [data]),
    historical = useMemo(() => data?.history.filter((x) => x.endedAt !== null) ?? [], [data]);
  if (state !== 'success' || !data)
    return <StatePanel state={canonicalStates.find((x) => x.kind === state)!} />;
  return (
    <div className="member-aliquots" role="tabpanel" aria-label="Cupos del socio">
      {actionSuccess ? (
        <div className="ui-alert ui-alert--success member-aliquots__action-success" role="status">
          <strong>{actionSuccess.title}</strong>
          <span>{actionSuccess.message}</span>
        </div>
      ) : null}
      <CanonicalMetricGrid>
        <CanonicalMetricCard
          label="Cupos activos"
          value={active.length}
          icon={<Layers3 aria-hidden="true" />}
          tone="success"
          detail="Cada una genera obligaciones independientes"
        />
        <CanonicalMetricCard
          label="Titularidades históricas"
          value={historical.length}
          icon={<History aria-hidden="true" />}
          tone="info"
          detail="Asignaciones finalizadas conservadas"
        />
      </CanonicalMetricGrid>
      <section className="member-aliquots__card">
        <header>
          <div>
            <span>CUPOS VIGENTES</span>
            <h2>Cupos activos</h2>
          </div>
          <div className="member-aliquots__actions">
            <StatusBadge tone="success">{active.length} activas</StatusBadge>
            <Button
              variant="primary"
              icon={<Plus aria-hidden="true" />}
              disabled={!data.assignable?.length}
              onClick={() => setAdding(true)}
            >
              Agregar cupo
            </Button>
          </div>
        </header>
        {active.length ? (
          <div className="member-aliquots__grid">
            {active.map((item) => (
              <article key={item.id} className="member-aliquots__item">
                <div className="member-aliquots__number">
                  <span>CUPO</span>
                  <strong>{item.number}</strong>
                </div>
                <div className="member-aliquots__content">
                  <dl>
                    <div>
                      <dt>Estado</dt>
                      <dd>
                        <StatusBadge tone="success">Activa</StatusBadge>
                      </dd>
                    </div>
                    <div>
                      <dt>Base de obligaciones</dt>
                      <dd>
                        {item.firstChargePeriod ?? 'Pendiente de definir'}{' '}
                        {item.admissionDateStatus ? (
                          <StatusBadge
                            tone={
                              item.admissionDateStatus === 'provisional' ? 'warning' : 'success'
                            }
                          >
                            {item.admissionDateStatus === 'provisional'
                              ? 'Provisional'
                              : 'Verificada'}
                          </StatusBadge>
                        ) : null}
                      </dd>
                    </div>
                    <div>
                      <dt>Motivo</dt>
                      <dd>{item.reason ?? 'Sin motivo registrado'}</dd>
                    </div>
                    <div>
                      <dt>Registrado por</dt>
                      <dd>{item.actor ?? 'Sistema'}</dd>
                    </div>
                  </dl>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setActionSuccess(null);
                      setVerifying(item);
                    }}
                  >
                    {item.admissionDateStatus === 'provisional'
                      ? 'Verificar fecha'
                      : 'Editar fecha'}
                  </Button>
                  <Button
                    icon={<ArrowRightLeft aria-hidden="true" />}
                    onClick={() => {
                      setActionSuccess(null);
                      setTransfer(item);
                    }}
                  >
                    Transferir cupo
                  </Button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="member-aliquots__empty">Este socio no tiene cupos activos.</div>
        )}
      </section>
      {historical.length ? (
        <section className="member-aliquots__card">
          <header>
            <div>
              <span>TRAZABILIDAD</span>
              <h2>Historial de titularidad</h2>
            </div>
          </header>
          <div className="member-aliquots__history">
            {historical.map((item) => (
              <article key={item.id}>
                <strong>Cupo {item.number}</strong>
                <span>
                  {formatDateTime(item.startedAt)} —{' '}
                  {item.endedAt ? formatDateTime(item.endedAt) : 'Actual'}
                </span>
                <small>{item.reason ?? 'Sin motivo registrado'}</small>
              </article>
            ))}
          </div>
        </section>
      ) : null}
      <CanonicalModal
        open={adding}
        size="medium"
        eyebrow="ADQUISICIÓN AUDITADA"
        title="Agregar o transferir cupo"
        description="Busca por número de cupo o por el titular actual. La operación conserva la evidencia y el historial."
        onClose={() => setAdding(false)}
      >
        <AcquireForm
          assignable={data.assignable ?? []}
          memberId={memberId}
          onDone={() => {
            setAdding(false);
            setRevision((v) => v + 1);
          }}
        />
      </CanonicalModal>
      <CanonicalModal
        open={Boolean(transfer)}
        size="medium"
        eyebrow="TRANSFERENCIA AUDITADA"
        title={`Transferir cupo ${transfer?.number ?? ''}`}
        description="La titularidad anterior se conserva. Las obligaciones se calcularán por propietario y período."
        onClose={closeTransfer}
      >
        {transfer ? (
          <TransferForm
            memberId={memberId}
            assignment={transfer}
            initialBuyerId={resumeBuyerId ?? ''}
            initialQuery={resumeBuyerQuery}
            onDone={(successMessage) => {
              setActionSuccess({ title: 'Transferencia completada', message: successMessage });
              closeTransfer();
              setRevision((v) => v + 1);
            }}
          />
        ) : null}
      </CanonicalModal>
      <CanonicalModal
        open={Boolean(verifying)}
        size="medium"
        eyebrow="FECHA DE INGRESO AUDITADA"
        title={`Verificar fecha · Cupo ${verifying?.number ?? ''}`}
        description="Registra la fecha real y su respaldo. El sistema identificará las obligaciones afectadas."
        onClose={() => setVerifying(null)}
      >
        {verifying ? (
          <VerifyAdmissionForm
            memberId={memberId}
            assignment={verifying}
            onDone={(successMessage) => {
              setActionSuccess({ title: 'Fecha guardada correctamente', message: successMessage });
              setVerifying(null);
              setRevision((value) => value + 1);
            }}
          />
        ) : null}
      </CanonicalModal>
    </div>
  );
}
function VerifyAdmissionForm({
  memberId,
  assignment,
  onDone,
}: {
  memberId: string;
  assignment: Assignment;
  onDone: (message: string) => void;
}) {
  const [joinedOn, setJoinedOn] = useState(assignment.firstChargePeriod?.slice(0, 10) ?? ''),
    [reference, setReference] = useState(''),
    [reason, setReason] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const ready = Boolean(
    joinedOn &&
    joinedOn <= '2024-10-24' &&
    reference.trim().length >= 3 &&
    reason.trim().length >= 10 &&
    file?.type === 'application/pdf',
  );
  const save = async () => {
    if (!ready || !file) return;
    setBusy(true);
    setMessage('');
    try {
      await verifyMemberAdmission(apiBase, memberId, {
        assignmentId: assignment.id,
        joinedOn,
        reference,
        reason,
        file,
      });
      onDone(`La fecha del cupo ${assignment.number} se registró y quedó auditada.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No se pudo verificar la fecha.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="aliquot-transfer-form">
      <label>Fecha real de ingreso *</label>
      <input
        type="date"
        max="2024-10-24"
        value={joinedOn}
        onChange={(event) => setJoinedOn(event.target.value)}
      />
      <label>Referencia del documento *</label>
      <input
        value={reference}
        maxLength={200}
        onChange={(event) => setReference(event.target.value)}
        placeholder="Ej. Acta o solicitud de ingreso"
      />
      <label>Motivo y fuente de la corrección *</label>
      <textarea
        rows={3}
        maxLength={500}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Describe el documento revisado y la razón del cambio"
      />
      <CanonicalFileDropzone
        label="Documento de respaldo en PDF *"
        file={file}
        onFileChange={setFile}
        onError={setMessage}
        helpText="Arrastra aquí el PDF o selecciónalo. Máximo 20 MB."
      />
      <div className="aliquot-transfer-preview">
        <strong>Vista previa</strong>
        <span>
          El cupo <b>{assignment.number}</b> cambiará su base de obligaciones de{' '}
          <b>{assignment.firstChargePeriod ?? 'sin fecha'}</b> a{' '}
          <b>{joinedOn ? joinedOn.slice(0, 7) : '—'}</b>.
        </span>
        <small>
          La evidencia y el cambio quedan auditados. Las obligaciones existentes no se eliminan: se
          marcarán para recálculo.
        </small>
      </div>
      {message ? (
        <div className="ui-alert ui-alert--error aliquot-action-form__result" role="alert">
          <strong>No se pudo guardar la fecha</strong>
          <span>{message}</span>
        </div>
      ) : null}
      <Button type="button" variant="primary" disabled={!ready || busy} onClick={() => void save()}>
        {busy ? 'Procesando guardado…' : 'Guardar fecha corregida'}
      </Button>
    </div>
  );
}
function TransferForm({
  memberId,
  assignment,
  initialBuyerId,
  initialQuery,
  onDone,
}: {
  memberId: string;
  assignment: Assignment;
  initialBuyerId: string;
  initialQuery: string;
  onDone: (message: string) => void;
}) {
  const [query, setQuery] = useState(initialQuery),
    [members, setMembers] = useState<readonly MemberListItemContract[]>([]),
    [buyer, setBuyer] = useState(initialBuyerId),
    [date, setDate] = useState(''),
    [reference, setReference] = useState(''),
    [reason, setReason] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  useEffect(() => {
    const c = new AbortController();
    const timer = setTimeout(() => {
      void getMembers(
        apiBase,
        { query, status: 'active', sort: 'name', direction: 'asc', page: 1, pageSize: 20 },
        c.signal,
      )
        .then((r) => setMembers(r.items.filter((m) => m.id !== memberId)))
        .catch(() => setMembers([]));
    }, 180);
    return () => {
      clearTimeout(timer);
      c.abort();
    };
  }, [query, memberId]);
  const ready = Boolean(
    buyer &&
    date &&
    date <= '2024-10-24' &&
    reference.trim().length >= 3 &&
    reason.trim().length >= 10 &&
    file?.type === 'application/pdf',
  );
  const month = date ? date.slice(0, 7) : '—';
  const save = async () => {
    if (!ready || !file) return;
    setBusy(true);
    setMessage('');
    try {
      await transferMemberAliquot(apiBase, memberId, assignment.id, {
        buyerMemberId: buyer,
        effectiveOn: date,
        reference,
        reason,
        file,
      });
      const selectedBuyer = members.find((member) => member.id === buyer);
      const buyerName = selectedBuyer
        ? `${selectedBuyer.familyNames} ${selectedBuyer.givenNames}`
        : 'el nuevo titular';
      onDone(`El cupo ${assignment.number} fue transferido correctamente a ${buyerName}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="aliquot-transfer-form">
      <SearchSelectField
        label="Nuevo titular"
        required
        placeholder="Buscar socio activo"
        value={buyer}
        onQueryChange={setQuery}
        onValueChange={setBuyer}
        options={members.map((m) => ({
          value: m.id,
          label: `${m.familyNames} ${m.givenNames}`,
          meta: `${m.nationalId ? `Cédula ${m.nationalId}` : 'Documentación pendiente'}${m.slotNumbers?.length ? ` · Cupos ${m.slotNumbers.join(', ')}` : ''}`,
        }))}
      />
      <Button
        variant="secondary"
        icon={<Plus aria-hidden="true" />}
        onClick={() => {
          window.location.hash = `#socios/nuevo?return=transfer&source=${memberId}&assignment=${assignment.id}`;
        }}
      >
        Crear nuevo socio y continuar
      </Button>
      <label>Fecha efectiva de adquisición *</label>
      <input type="date" max="2024-10-24" value={date} onChange={(e) => setDate(e.target.value)} />
      <label>Comprobante o referencia *</label>
      <input
        value={reference}
        maxLength={200}
        onChange={(e) => setReference(e.target.value)}
        placeholder="Ej. Contrato de cesión 014"
      />
      <label>Motivo de la transferencia *</label>
      <textarea
        rows={3}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Describe la compra y la evidencia revisada"
      />
      <CanonicalFileDropzone
        label="Evidencia de compra en PDF *"
        file={file}
        onFileChange={setFile}
        onError={setMessage}
        helpText="Arrastra aquí el PDF o selecciónalo. Máximo 20 MB."
      />
      <div className="aliquot-transfer-preview">
        <strong>Vista previa</strong>
        <span>
          El nuevo titular recibirá obligaciones desde <b>{month}</b> hasta <b>2024-10</b>,
          inclusive.
        </span>
        <small>
          Se generarán las obligaciones aplicables hasta octubre de 2024, sin duplicar cargos
          existentes.
        </small>
      </div>
      {message ? (
        <div className="ui-alert ui-alert--error aliquot-action-form__result" role="alert">
          <strong>No se pudo completar la transferencia</strong>
          <span>{message}</span>
        </div>
      ) : null}
      <Button type="button" variant="primary" disabled={!ready || busy} onClick={() => void save()}>
        {busy ? 'Procesando transferencia…' : 'Confirmar transferencia'}
      </Button>
    </div>
  );
}

function AcquireForm({
  memberId,
  assignable,
  onDone,
}: {
  memberId: string;
  assignable: NonNullable<MemberNumberResponse['assignable']>;
  onDone: () => void;
}) {
  const [slotQuery, setSlotQuery] = useState(''),
    [number, setNumber] = useState(''),
    [date, setDate] = useState(''),
    [reference, setReference] = useState(''),
    [reason, setReason] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const selected = assignable.find((item) => item.number === number);
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('es')
      .trim();
  const normalizedQuery = normalize(slotQuery);
  const matches =
    normalizedQuery.length < 2
      ? []
      : assignable
          .filter((item) =>
            normalize(`${item.number} ${item.ownerName ?? ''}`).includes(normalizedQuery),
          )
          .slice(0, 20);
  const ready = Boolean(
    selected &&
    date &&
    date <= '2024-10-24' &&
    reference.trim().length >= 3 &&
    reason.trim().length >= 10 &&
    file?.type === 'application/pdf',
  );
  const save = async () => {
    if (!ready || !file) return;
    setBusy(true);
    setMessage('');
    try {
      if (selected?.assignmentId && selected.memberId) {
        await transferMemberAliquot(apiBase, selected.memberId, selected.assignmentId, {
          buyerMemberId: memberId,
          effectiveOn: date,
          reference,
          reason,
          file,
        });
      } else {
        await acquireAvailableAliquot(apiBase, memberId, {
          number,
          effectiveOn: date,
          reference,
          reason,
          file,
        });
      }
      onDone();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="aliquot-transfer-form">
      {message ? (
        <div className="ui-alert" role="alert">
          {message}
        </div>
      ) : null}
      <SearchSelectField
        label="Buscar cupo o titular actual"
        hint="Escribe al menos 2 caracteres. No se muestra el padrón completo."
        required
        placeholder="Ej. 137 o ANDRADE PRESENTACIÓN"
        emptyMessage={
          normalizedQuery.length < 2
            ? 'Escribe un número o un nombre para buscar.'
            : 'No existe un cupo que coincida con la búsqueda.'
        }
        value={number}
        onQueryChange={setSlotQuery}
        onValueChange={setNumber}
        options={matches.map((item) => ({
          value: item.number,
          label: `Cupo ${item.number}`,
          meta: item.ownerName
            ? `Titular actual: ${item.ownerName} · se transferirá`
            : 'Disponible para asignación',
        }))}
      />
      <label>Fecha efectiva de adquisición *</label>
      <input type="date" max="2024-10-24" value={date} onChange={(e) => setDate(e.target.value)} />
      <label>Comprobante o referencia *</label>
      <input
        value={reference}
        maxLength={200}
        onChange={(e) => setReference(e.target.value)}
        placeholder="Ej. Contrato de compra 014"
      />
      <label>Motivo de la adquisición *</label>
      <textarea
        rows={3}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Describe la adquisición y la evidencia revisada"
      />
      <CanonicalFileDropzone
        label="Evidencia de compra en PDF *"
        file={file}
        onFileChange={setFile}
        onError={setMessage}
        helpText="Arrastra aquí el PDF o selecciónalo. Máximo 20 MB."
      />
      <div className="aliquot-transfer-preview">
        <strong>Vista previa</strong>
        <span>
          El cupo <b>{number || '—'}</b>{' '}
          {selected?.ownerName ? (
            <>
              se transferirá desde <b>{selected.ownerName}</b>, conservando su historial.
            </>
          ) : (
            <>se agregará como cupo disponible.</>
          )}
        </span>
        <span>
          Sus obligaciones corresponderán desde <b>{date ? date.slice(0, 7) : '—'}</b> hasta{' '}
          <b>2024-10</b>, inclusive.
        </span>
        <small>
          Guardar registra titularidad, evidencia y auditoría; las obligaciones se generan en su
          proceso correspondiente.
        </small>
      </div>
      <Button variant="primary" disabled={!ready || busy} onClick={() => void save()}>
        {busy ? 'Agregando cupo…' : 'Confirmar adquisición'}
      </Button>
    </div>
  );
}
